import type { Ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';

import { useConfig } from '@modules/config/composables';
import { TRANSLATION_GROUP_INTERFACE_LANGUAGES } from '@modules/i18n/constants';

import type { ILanguage } from '../types';
import useI18n from './useI18n';

export interface LanguagesComposable {
  interfaceLanguages: Ref<ILanguage[]>;
  creativeLanguages: Ref<ILanguage[]>;
  allLanguages: Ref<ILanguage[]>;
}

export default function useLanguages(): LanguagesComposable {
  const { t } = useTranslation();
  const { config } = useConfig();
  const { getTranslation } = useI18n();

  const interfaceLanguages = computed<ILanguage[]>(() => (
    (config.value?.enabled_locales.interface_locales ?? [])
      .map((locale) => ({
        value: locale,
        label: getTranslation(TRANSLATION_GROUP_INTERFACE_LANGUAGES, `LANGUAGES_${locale}`).value,
      }))
  ));

  const creativeLanguages = computed<ILanguage[]>(() => (
    (config.value?.enabled_locales.creative_locales ?? [])
      .map((locale) => ({
        value: locale,
        label: t(`creative.list_locales.${locale}`),
      }))
  ));

  const allLanguages = computed<ILanguage[]>(() => (
    (config.value?.list_locales ?? [])
      .map((locale) => ({
        value: locale,
        label: getTranslation(TRANSLATION_GROUP_INTERFACE_LANGUAGES, `LANGUAGES_${locale}`).value,
      }))
  ));

  return {
    interfaceLanguages,
    creativeLanguages,
    allLanguages,
  };
}
